import React, { useRef, useState } from "react";
import { api } from "../lib/api";
import { Upload, X, Loader2, ImageIcon } from "lucide-react";
import { toast } from "sonner";

/**
 * PhotoUpload — multi-image picker with previews.
 * Props:
 *  - value: array of {url, path} already uploaded
 *  - onChange: callback with new array
 *  - max: int (default 5)
 */
const PhotoUpload = ({ value = [], onChange, max = 5 }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  const handleFiles = async (files) => {
    const filesArr = Array.from(files || []);
    if (!filesArr.length) return;
    if (value.length + filesArr.length > max) {
      toast.error(`Maximum ${max} photos`);
      return;
    }
    setUploading(true);
    const uploaded = [...value];
    for (const f of filesArr) {
      if (!f.type.startsWith("image/")) {
        toast.error(`Skipped ${f.name}: not an image`);
        continue;
      }
      if (f.size > 8 * 1024 * 1024) {
        toast.error(`Skipped ${f.name}: > 8 MB`);
        continue;
      }
      try {
        const form = new FormData();
        form.append("file", f);
        const res = await api.post("/uploads", form, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ url: `${BACKEND_URL}${res.data.url}`, path: res.data.path });
      } catch (e) {
        const status = e?.response?.status;
        if (status === 503) {
          toast.error("Uploads disabled — backend EMERGENT_LLM_KEY not set yet");
          break;
        }
        toast.error(`Upload failed for ${f.name}`);
      }
    }
    onChange(uploaded);
    setUploading(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));

  return (
    <div data-testid="photo-upload">
      <label className="block text-sm font-heading font-medium text-[#2D3748] mb-2">
        Photos <span className="text-[#718096] font-normal">(optional, up to {max})</span>
      </label>
      <div
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
        className="border-2 border-dashed border-[#E2E8F0] hover:border-[#25D366] rounded-xl p-6 text-center cursor-pointer bg-[#F7FAFC] transition-colors"
        data-testid="photo-upload-dropzone"
      >
        {uploading ? (
          <div className="flex items-center justify-center gap-2 text-[#718096]">
            <Loader2 className="w-4 h-4 animate-spin" /> Uploading…
          </div>
        ) : (
          <>
            <Upload className="w-6 h-6 text-[#25D366] mx-auto" />
            <div className="mt-2 text-sm font-heading font-medium text-[#2D3748]">Click or drag photos here</div>
            <div className="text-xs text-[#718096] mt-1">JPEG, PNG, WebP · max 8 MB each</div>
          </>
        )}
        <input
          ref={inputRef}
          data-testid="photo-upload-input"
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          onChange={(e) => handleFiles(e.target.files)}
        />
      </div>
      {value.length > 0 && (
        <div className="mt-3 grid grid-cols-3 sm:grid-cols-5 gap-2">
          {value.map((p, i) => (
            <div key={p.path || i} className="relative aspect-square rounded-lg overflow-hidden border border-[#E2E8F0] bg-[#F7FAFC]" data-testid={`uploaded-photo-${i}`}>
              <img src={p.url} alt="" className="w-full h-full object-cover" />
              <button
                type="button"
                onClick={() => remove(i)}
                data-testid={`remove-photo-${i}`}
                className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/95 hover:bg-white text-[#DC2626] flex items-center justify-center shadow"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          {value.length < max && (
            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              className="aspect-square rounded-lg border-2 border-dashed border-[#E2E8F0] flex items-center justify-center text-[#718096] hover:border-[#25D366] hover:text-[#25D366]"
              data-testid="add-more-photos"
            >
              <ImageIcon className="w-5 h-5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default PhotoUpload;
